import type {
	AvailableDownPaymentProductsResponse,
	AvailableInsuranceActivationProductsResponse,
	AvailableInsuranceProductsResponse,
	AvailableSwapActivationProductsResponse,
	CustomerSubscriptionsOverviewResponse,
	DownPaymentSubscriptionsResponse,
	InsuranceSubscriptionsResponse,
	ProofOfInsuranceResponse,
	SubmitInsuranceActivationResponse,
	SwapSubscriptionsResponse,
	ValidateInsuranceActivationResponse,
} from "~/ts-integrations/hardware-subscriptions-v1";
import apigeeIntegration from "./self-service-adapter";

const api = apigeeIntegration("/hardware-subscriptions/v1");

interface GetInsuranceProductsParams {
	imei?: string | number;
	gtin?: string | number;
	hardwareType?: string;
	owner?: string;
	subscriptionProductOfferId: number;
}

const getInsuranceProducts = async ({
	imei,
	gtin,
	hardwareType = "MOBILE_PHONE",
	owner,
	subscriptionProductOfferId,
}: GetInsuranceProductsParams): Promise<AvailableInsuranceActivationProductsResponse> => {
	const queryParams: Record<string, string | undefined> = {
		imei: imei !== undefined ? String(imei) : undefined,
		gtin: gtin !== undefined ? String(gtin) : undefined,
		hardwareType,
		owner,
	};
	const filteredQueryParams = Object.fromEntries(
		Object.entries(queryParams).filter(([_, v]) => v !== undefined)
	);
	const query = new URLSearchParams(filteredQueryParams);

	return api.get(`/mobile-subscription-products/${subscriptionProductOfferId}/insurance-products?${query}`);
};

const getOverviewOfAgreements = async (): Promise<CustomerSubscriptionsOverviewResponse> => {
	return api.get("/users/myself/overview");
};

const getSwapDevices = async (): Promise<SwapSubscriptionsResponse> => {
	return api.get("/users/myself/swap-subscriptions");
};

const getDownPaymentDevices = async (): Promise<DownPaymentSubscriptionsResponse> => {
	return api.get("/users/myself/downpayment-subscriptions");
};

interface GetSwapAgreementWithSubscriptionParams {
	subscriptionProductId: number | string;
	monthlyListPrice: number | string;
}

const getSWAPAgreementWithSubscription = async ({
	subscriptionProductId,
	monthlyListPrice,
}: GetSwapAgreementWithSubscriptionParams): Promise<AvailableSwapActivationProductsResponse> => {
	return api.get(
		`/mobile-subscription-products/${subscriptionProductId}/swap-products?monthlyListPrice=${monthlyListPrice}`,
	);
};

const getSWAPAgreementWithoutSubscription = async (
	monthlyListPrice: number | string,
): Promise<AvailableSwapActivationProductsResponse> => {
	return api.get(`/hardware-subscription-products/swap-products?monthlyListPrice=${monthlyListPrice}`);
};

interface GetDownPaymentAgreementWithSubscriptionParams {
	subscriptionProductId: number | string;
	totalPrice: number | string;
}

const getDownPaymentAgreementWithSubscription = async ({
	subscriptionProductId,
	totalPrice,
}: GetDownPaymentAgreementWithSubscriptionParams): Promise<AvailableDownPaymentProductsResponse> => {
	return api.get(
		`/mobile-subscription-products/${subscriptionProductId}/downpayment-products?totalPrice=${totalPrice}`,
	);
};

const getDownPaymentAgreementWithoutSubscription = async (
	totalPrice: number | string,
): Promise<AvailableDownPaymentProductsResponse> => {
	return api.get(`/hardware-subscription-products/downpayments-products?totalPrice=${totalPrice}`);
};

const getUsersInuredDevices = async (): Promise<InsuranceSubscriptionsResponse> => {
	return api.get("users/myself/insurance-subscriptions", { cache: { override: true } } as any);
};

const getProofOfInsurance = async (imei: string | number): Promise<ProofOfInsuranceResponse> => {
	return api.get(`insurance-subscriptions/${imei}/proof-of-insurance`);
};

const getInsuranceProductsForUpgradingOrDowngrading = async (
	imei: string | number,
): Promise<AvailableInsuranceProductsResponse> => {
	return api.get(`insurance-subscriptions/${imei}/available-products`, { cache: { override: true } } as any);
};

interface ValidateChangeParams {
	imei: string | number;
	newInsuranceProductId: number;
}

const validateChange = async ({
	imei,
	newInsuranceProductId,
}: ValidateChangeParams): Promise<ValidateInsuranceActivationResponse> => {
	return api.put(`insurance-subscriptions/${imei}/validation`, { newInsuranceProductId: newInsuranceProductId });
};

const validatePostPurchaseInsurance = async (imei: string | number): Promise<AvailableInsuranceProductsResponse> => {
	return api.post(`insurance-subscriptions/${imei}/validate-post-purchase-insurance`);
};

const submitPostPurchaseInsurance = async (imei: string | number): Promise<SubmitInsuranceActivationResponse> => {
	return api.post(`insurance-subscriptions/${imei}/submit-post-purchase-insurance`);
};

const getOrderableInsuranceProducts = async (imei: string | number): Promise<unknown> => {
	return api.post(`insurance-subscriptions/${imei}/orderable-products`);
};

interface ChangeInsuranceParams {
	imei: string | number;
	newInsuranceProductId: number;
}

const changeInsurance = async ({
	imei,
	newInsuranceProductId,
}: ChangeInsuranceParams): Promise<SubmitInsuranceActivationResponse> => {
	return api.put(`insurance-subscriptions/${imei}`, { newInsuranceProductId: newInsuranceProductId });
};

interface FetchSefTemplateParams {
	imei?: string | number;
	credit_amount: number;
	number_of_months: 24 | 36;
	monthly_amount: number;
	cash_price: number;
}

const fetchSefTemplate = async ({
	imei,
	credit_amount,
	number_of_months,
	monthly_amount,
	cash_price,
}: FetchSefTemplateParams): Promise<string> => {
	return api.post(
		`/documents/secci`,
		{
			creditAmount: credit_amount,
			numberOfMonthlyInstallments: number_of_months,
			monthlyAmount: monthly_amount,
			cashPrice: cash_price,
			imei: imei,
			print: true,
		},
		{
			headers: {
				Accept: "application/pdf",
			},
			responseType: "arraybuffer",
		},
	);
};

export default {
	getInsuranceProducts,
	getOverviewOfAgreements,
	getSwapDevices,
	getSWAPAgreementWithSubscription,
	getSWAPAgreementWithoutSubscription,
	getUsersInuredDevices,
	getProofOfInsurance,
	getInsuranceProductsForUpgradingOrDowngrading,
	changeInsurance,
	validateChange,
	getOrderableInsuranceProducts,
	getDownPaymentDevices,
	getDownPaymentAgreementWithoutSubscription,
	getDownPaymentAgreementWithSubscription,
	fetchSefTemplate,
	validatePostPurchaseInsurance,
	submitPostPurchaseInsurance,
};
